import React, { useState, useEffect, useCallback, useRef } from 'react';
import type { BrandConfig } from '@lds-efsp/brand-config';
import type { IconsType } from '../../../types/admin';
import type { Account, AccountUser, Office } from '@lds-efsp/shared-contracts';
import {
  CUSTOMER_CATEGORIES,
  CUSTOMER_CATEGORY_LABELS,
  customerCategoryDisplayLabel,
  decideMemberStateChange,
  MEMBER_STATES,
  type IdentityApplyRequest,
  type IdentityDifferencesResponse,
  type MemberIdentityDifference,
  type MemberState,
} from '@lds-efsp/shared-contracts';
import {
  SetupDetailDrawer,
  SetupFilterBar,
  SetupDataTable,
  StatusBadge,
  SetupItemPreviewPopover,
} from '../components';
import FormInput, { FormSelect, FormTextarea, FormCheckbox, FormRadioGroup } from '../components/FormInput';
import { useApi, ApiError } from '../../../api/useApi';

interface SetupAccountsProps {
  brand: BrandConfig;
  Icons: IconsType;
}

const accountTypeOptions = [
  { value: 'individual', label: 'Individual' },
  { value: 'organization', label: 'Organization' },
];

// Customer category (SOR-129 R18): the six labels in canonical vocabulary
// order plus an explicit unset option. Never labeled "Account Type" — that
// name is taken twice by the accountType controls below (I6).
const customerCategoryOptions = [
  { value: '', label: '-- Unset --' },
  ...CUSTOMER_CATEGORIES.map((category) => ({
    value: category as string,
    label: CUSTOMER_CATEGORY_LABELS[category],
  })),
];

const paymentMethodOptions = [
  { value: 'credit-card', label: 'Credit Card' },
  { value: 'ach', label: 'ACH' },
  { value: 'check', label: 'Check' },
  { value: 'invoice', label: 'Invoice' },
  { value: 'prepaid', label: 'Prepaid' },
];

const paymentFrequencyOptions = [
  { value: 'per-order', label: 'Per Order' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
];

const paymentStatusOptions = [
  { value: 'current', label: 'Current' },
  { value: 'past-due', label: 'Past Due' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'prepaid', label: 'Prepaid' },
];

const accountStatusOptions = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'closed', label: 'Closed' },
];

const statusFilterOptions = [
  ...accountStatusOptions,
];

const preAuthOptions = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
];

const processingFeeModeOptions = [
  { value: 'default', label: 'Default (Open Credit)' },
  { value: 'custom', label: 'Custom (Open Credit)' },
];

const accountUserRoleOptions = [
  { value: 'owner', label: 'Owner' },
  { value: 'admin', label: 'Admin' },
  { value: 'member', label: 'Member' },
  { value: 'viewer', label: 'Viewer' },
];

// Member-state control (SOR-590 R1). The states a staff actor may offer a
// membership row are DERIVED, never listed: the row's current state plus
// exactly the targets the shared decider applies for staff. A hand-written
// list here would be a second decider beside `decideMemberStateChange`, and
// the two would drift the first time a move changed. The reserved states
// (`suspended`, `erased`) have no staff-applied target, so a row sitting in
// one yields the single-option — and therefore read-only — control below.
const memberStateLabels: Record<MemberState, string> = {
  pending: 'Pending',
  active: 'Active',
  inactive: 'Inactive',
  suspended: 'Suspended',
  erased: 'Erased',
};

function memberStateOptions(prior: MemberState): { value: string; label: string }[] {
  return MEMBER_STATES.filter(
    (state) => state === prior || decideMemberStateChange(prior, state, 'staff') === 'applied',
  ).map((state) => ({ value: state, label: memberStateLabels[state] }));
}

/**
 * Whether a member row is beyond every staff move — the reserved states
 * (`suspended`, `erased`), which the decider refuses for a staff actor. Read
 * off the SAME option set the state control renders rather than from a second
 * list, so the row's Delete is offered exactly when the control beside it
 * could move the row: one question ("can staff move this row at all?"), one
 * answer, asked once here and once per row.
 */
function isReservedMemberState(prior: MemberState): boolean {
  return memberStateOptions(prior).length === 1;
}

// ── Auth0 identity differences (SPEC-SOR-600-v4 R1/R2) ──────────────────────
//
// The reconciliation surface's read, as `GET …/users/identity-differences`
// answers it: one row per member who has something to reconcile, each part
// independently present or absent (`identityDifference` decides both; the route
// only carries its verdict). The shapes are the API's own wire contract and
// live in @lds-efsp/shared-contracts — imported, never re-declared, so this
// page cannot render (or post) a part the API would never send.

/**
 * The Apply body's key for one offered part. Each part is applied on its own —
 * the screen confirms the email and the names separately — so a part's key is
 * the button that posts it and the value is exactly the `theirs` value that
 * part's row shows.
 */
type IdentityApplyPart = 'email' | 'names';

/**
 * One member's in-flight Apply, as the screen tracks it: the part whose own
 * control was pressed, and the token naming THAT request.
 *
 * The token is what makes the entry the request's own. The map below is dropped
 * wholesale when the drawer moves, so the same member's same part can be
 * pressed again while the first Apply is still out — and the two entries are
 * then indistinguishable by member and part alone. An answer settles the entry
 * only while its token still stands; without it, the older answer's cleanup
 * would clear the newer request's entry and re-enable the member's controls
 * with a write still in flight, which is the race the map exists to close.
 */
interface IdentityApplyInFlight {
  part: IdentityApplyPart;
  token: number;
}

/**
 * A name pair as one line for the comparison view. An offer's `ours` may be
 * half-empty (the row carries only one of the two names), and the display must
 * stay readable rather than print a stray space; a pair with nothing in it at
 * all is a dash, matching the other blank cells on this screen.
 */
function displayName(firstName: string | null | undefined, lastName: string | null | undefined): string {
  const joined = [firstName, lastName]
    .filter((part): part is string => typeof part === 'string' && part.trim() !== '')
    .join(' ');
  return joined === '' ? '—' : joined;
}

/**
 * The member's entry with the part just applied removed — or `null` when
 * nothing is left to offer, which drops the entry entirely.
 *
 * An Apply's own 200 says the part was written, so the screen stops offering it
 * AT ONCE rather than at the differences re-read that same 200 fires: that read
 * is a second Auth0 round trip, and until it lands the row would still carry an
 * Apply for a value the member no longer differs by — the button that was just
 * pressed, pressable again. Only the part whose own control was pressed goes:
 * an Apply confirms one part, so the other, if it was offered, stays exactly as
 * pressable as it was.
 */
function withoutAppliedPart(
  offer: MemberIdentityDifference,
  part: IdentityApplyPart,
): MemberIdentityDifference | null {
  const settled: MemberIdentityDifference = {
    ...offer,
    email: part === 'email' ? null : offer.email,
    names: part === 'names' ? null : offer.names,
  };
  return settled.email === null && settled.names === null ? null : settled;
}

// ── Saving only what changed (SOR-615) ──────────────────────────────────────
//
// Both forms on this screen keep a BASELINE: the record's values as the form
// last took them from the server. A Save sends only the fields that differ from
// it, and a re-read of the record behind an open form folds into the form
// against it. Before this, both forms posted a whole snapshot, so a record
// changed behind an open form — an identity Apply writing the account's contact
// email or a member's address — was written straight back by the next Save
// (WOL-492: the member PATCH carried the pre-Apply email).
//
// "Edited" has ONE answer, asked by all three of the diff, the merge and the
// member's two — `fieldEdited` below — so the set of fields a Save sends and
// the set a re-read must not overwrite cannot drift apart.

/** The parts of an address, in the order the Profile form renders them. */
const ADDRESS_PARTS: (keyof NonNullable<Account['address']>)[] = [
  'street1',
  'street2',
  'city',
  'state',
  'zip',
  'country',
];

/**
 * An address with nothing in it is NO address: each part is compared as the
 * empty string the form would render it as, so an address every part of which
 * is blank normalizes to `undefined` — the value a record that carries no
 * address holds. Without this, an account with no address whose empty Street
 * field the staff member typed into and then cleared reads as edited, and a
 * Save sends the record a blank address it never had (SOR-617).
 */
function normaliseAddress(
  address: Account['address'] | undefined,
): Account['address'] | undefined {
  if (!address) return undefined;
  return ADDRESS_PARTS.some((part) => address[part]) ? address : undefined;
}

/**
 * Whether one top-level field of `form` still matches `baseline` — the
 * question "has the staff member edited this field?".
 *
 * An ABSENT value and an empty string are the same value: the form renders a
 * field the record does not carry as an empty control, so typing into it and
 * deleting the text again leaves the record unedited rather than sending a
 * PATCH carrying the empty string the record never had (SOR-617). One rule for
 * every scalar field, because the diff and the re-read ask this one question.
 *
 * Every field but `address` compares by value; `address` compares BY PART,
 * because the form edits it one part at a time and typing a city is an edit to
 * the address, not a string that happens to be nested. Its absent parts read as
 * the empty string the form renders them as, its blank parts collapse to no
 * address at all (`normaliseAddress`), and an address present on one side and
 * absent on the other is an edit.
 */
function fieldEdited(
  form: Record<string, unknown>,
  baseline: Record<string, unknown>,
  key: string,
): boolean {
  if (key === 'address') {
    const a = normaliseAddress(form.address as Account['address'] | undefined);
    const b = normaliseAddress(baseline.address as Account['address'] | undefined);
    if (!a || !b) return Boolean(a) !== Boolean(b);
    return ADDRESS_PARTS.some((part) => (a[part] ?? '') !== (b[part] ?? ''));
  }
  return (form[key] ?? '') !== (baseline[key] ?? '');
}

/**
 * The Profile form's PATCH body: only the top-level fields whose form value
 * differs from the Profile baseline. A field the staff member never touched is
 * left out entirely, so a record changed behind the open form is not written
 * back by a Save.
 *
 * `address` rides the same question but is sent WHOLE: the update path replaces
 * the address from the frame it is given, so a change to any one part means
 * every part has to travel.
 *
 * `customerCategory` keeps the rule — and the reason for it — the diff-aware
 * save already carried: the update mapper stamps admin provenance on ANY frame
 * carrying the key, so an untouched category must be omitted (re-sending it on
 * an unrelated save would silently freeze chain updates for the account), and a
 * cleared selection ('' from the select) goes out as an explicit null.
 */
function changedAccountFields(
  form: Partial<Account>,
  baseline: Partial<Account>,
): Record<string, unknown> {
  const formFields = form as Record<string, unknown>;
  const baselineFields = baseline as Record<string, unknown>;
  const payload: Record<string, unknown> = {};
  for (const key of Object.keys(formFields)) {
    // Both are decided below, by their own rules rather than the plain compare.
    if (key === 'address' || key === 'customerCategory') continue;
    if (fieldEdited(formFields, baselineFields, key)) payload[key] = formFields[key];
  }
  if (fieldEdited(formFields, baselineFields, 'address')) payload.address = form.address;
  const nextCategory = form.customerCategory || null;
  const prevCategory = baseline.customerCategory || null;
  if (nextCategory !== prevCategory) payload.customerCategory = nextCategory;
  return payload;
}

/**
 * The member form's PATCH body: only the fields whose form value differs from
 * the member baseline. `auth0Id` is why that matters as much as it does — a
 * linked member's row carries the Auth0 identity the route must keep, and the
 * form has no control for it, so an untouched id is simply never sent. Nothing
 * special-cases it: the general rule is the whole rule.
 */
function changedMemberFields(
  form: Partial<AccountUser>,
  baseline: Partial<AccountUser>,
): Record<string, unknown> {
  const formFields = form as Record<string, unknown>;
  const baselineFields = baseline as Record<string, unknown>;
  const payload: Record<string, unknown> = {};
  for (const key of Object.keys(formFields)) {
    if (fieldEdited(formFields, baselineFields, key)) payload[key] = formFields[key];
  }
  return payload;
}

/**
 * A fresh read of the record behind an OPEN form, folded into that form: each
 * field the staff member has not edited takes the fresh value, and each edited
 * field keeps theirs. So a re-read never overwrites what they are typing, and
 * never leaves the form holding a value the server has since replaced.
 *
 * The fresh record is the new baseline either way — including for the edited
 * fields — so the edit is still an edit AGAINST the server's copy and the next
 * Save sends exactly it. Whole-field, `address` included and compared by part:
 * an address the staff member has touched is theirs until they save it.
 */
function mergeFreshRecord<T extends object>(
  form: Partial<T>,
  baseline: Partial<T>,
  fresh: Partial<T>,
): Partial<T> {
  const formFields = form as Record<string, unknown>;
  const baselineFields = baseline as Record<string, unknown>;
  const merged: Record<string, unknown> = { ...(fresh as Record<string, unknown>) };
  for (const key of Object.keys(formFields)) {
    if (fieldEdited(formFields, baselineFields, key)) merged[key] = formFields[key];
  }
  return merged as Partial<T>;
}

type TabType = 'profile' | 'paymentInfo' | 'accountUsers';

const SetupAccounts: React.FC<SetupAccountsProps> = ({ brand, Icons }) => {
  const { get, post, patch, del } = useApi();
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState<Partial<Account>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('profile');
  const [error, setError] = useState<string | null>(null);

  // Account users state
  const [accountUsers, setAccountUsers] = useState<AccountUser[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [editingUser, setEditingUser] = useState<AccountUser | null>(null);
  const [isCreatingUser, setIsCreatingUser] = useState(false);
  const [userFormData, setUserFormData] = useState<Partial<AccountUser>>({});
  const [isSavingUser, setIsSavingUser] = useState(false);

  // The account the member surface is showing right now — set when a row is
  // opened (`handleRowClick`) and cleared on the create path (`handleAdd`),
  // where no account stands behind the form. Every read below checks it before
  // its `setState`, so an answer that arrives after the staff member has moved
  // on cannot land under the account now on screen. A ref rather than state
  // because the check is read inside the read's own continuation, not during a
  // render.
  const requestedAccountIdRef = useRef<string | null>(null);

  // The refresh generation: a monotonic stamp taken when a MEMBER/differences
  // read of the selected account starts, and re-checked when its answer lands.
  // The account id alone answers "is this still the account on screen?"; it
  // cannot tell two reads of the SAME account apart — two quick refreshes, or
  // leaving for another account and returning while the first account's read is
  // still slow — so an older answer could paint over the newer one. Bumped on
  // every selection change as well, so a read left in flight when the drawer
  // moved is dead even if that same account is opened again.
  const refreshGenerationRef = useRef(0);

  // The account-read stamp: the guard for `refreshAccount` alone, and a
  // separate counter from the refresh generation ON PURPOSE (SOR-610). It moves
  // when the SELECTION moves, and when a fresh account read starts — and for no
  // other reason. The member list and the differences are re-read on every save,
  // delete and refused Apply, which is exactly why the account read must not
  // ride the member generation: a member refresh landing while the Apply's
  // account read is still out would void it, and the Profile form would keep
  // the pre-Apply contact email a later Save then sends back. The account read
  // answers a different question — what the SERVER holds for this account — and
  // only a new selection or a newer account read supersedes it.
  const accountReadGenerationRef = useRef(0);

  // The account-LIST read generation: the identity of the newest read of the
  // accounts TABLE (`fetchAccounts`), so an older answer cannot paint over a
  // newer one. The table is the whole screen's — a save, a deactivate, a sync
  // and a search/filter change all re-read it, and none of them waits for the
  // last — so two reads can be out at once and, answering out of order, the
  // older would leave the table holding the state the newer read just replaced.
  // A counter of its OWN, not the member or account-read generation: those are
  // scoped to the DRAWER's account and move with the selection, while the
  // table's read belongs to the screen and is superseded only by another table
  // read. Bumped when a list read starts, and for no other reason.
  const accountListGenerationRef = useRef(0);

  // The Profile form's baseline: the account's values as the form last took
  // them from the server. A Save sends only what differs from it, and a re-read
  // folds into the form against it — so a record that moved behind the open form
  // (an identity Apply writing the contact email) is neither written back nor
  // allowed to overwrite an edit. A ref because it is read inside the save and
  // inside a read's own continuation, never during a render.
  const profileBaselineRef = useRef<Partial<Account>>({});

  // The member form's baseline, on the same terms — and its own COPY of the
  // row, never the row object in `accountUsers`: the member list is replaced
  // wholesale on every refresh, so a baseline aliasing the row would travel
  // with the list and the edit it was measured against would vanish.
  const memberBaselineRef = useRef<Partial<AccountUser>>({});

  // The member whose form is OPEN, by id — read when a member-list answer lands,
  // never captured when the read starts. `editingUser` cannot answer that: a
  // read fired while one member was open (a save's own refresh, an Apply from
  // the table) can answer after staff have opened ANOTHER member, and folding
  // the first member's fresh row into the second's form would rewrite a form
  // nobody edited and stamp a new member's blank form with an id it never had
  // (SOR-615). Set where the form opens and cleared where it closes — `Add`,
  // `Cancel`, a save, and a moved drawer — so it answers "which member's form is
  // open right now" at the instant the answer lands, the same way
  // `requestedAccountIdRef` answers for the account. A ref because it is read
  // inside a read's own continuation, never during a render.
  const openMemberIdRef = useRef<string | null>(null);

  // The member-FORM stamp: the identity of the form INSTANCE the member surface
  // holds open right now, taken afresh where a form opens (`handleAddUser`,
  // `handleEditUser`) and cleared to `null` where it closes (Cancel, a save's
  // own close, the drawer moving). `openMemberIdRef` answers which member a
  // re-read folds into; it CANNOT answer whether the form on screen is still the
  // one a Save was started for — every blank create form is `null`, and an edit
  // of the same member reopened after a Cancel carries the same id as the one
  // before it, so a late Save comparing ids would close a form the staff member
  // has since opened and typed into (SOR-616 review). The stamp is that answer:
  // a fresh instance every time a form opens, so its value is equal only for the
  // very form the Save was started on. A ref because it is read inside the
  // save's own continuation, never during a render.
  const openMemberFormStampRef = useRef<number | null>(null);

  // The monotonic source of those stamps — one fresh value per form opened, so
  // two form instances never share a value even when they name the same member
  // (or none). Never reset: a stamp is only ever compared for equality.
  const memberFormStampSeqRef = useRef(0);

  // Identity differences (SPEC-SOR-600-v4 R1/R2), keyed by member id: the
  // table asks for one member's offer per row. A member absent from this map
  // has nothing to reconcile, and the row shows neither values nor an Apply.
  const [identityDifferences, setIdentityDifferences] = useState<Record<string, MemberIdentityDifference>>({});

  // The members the read could not check (the answer's `unchecked`), by id.
  // NOT members with nothing to reconcile: the read did not complete for these,
  // so their difference is UNKNOWN and the row says "not checked" where a
  // difference would stand. A Set because a row asks membership of it, not the
  // order the read answered in.
  const [identityUnchecked, setIdentityUnchecked] = useState<ReadonlySet<string>>(new Set());

  // Whether the differences read itself failed. The offers are empty either
  // way, and an empty answer and an answer that never came are different facts:
  // rendered alike, a failed read would show every member as reconciled. The
  // screen states the failure instead.
  const [identityCheckFailed, setIdentityCheckFailed] = useState(false);

  // Which Apply control is in flight, by the member id whose row the write
  // rewrites: the part its own control is running, and the token naming that
  // request. Keyed by MEMBER, not by control: an Apply rewrites that member's
  // whole row, so while any part of it is out every control of that member is
  // disabled — a second part must not post values the first is about to
  // replace. One key for the whole screen (the shape this replaced) could name
  // only one control, so two members' Applies overwrote each other and
  // whichever answered cleared a state the other was still owed.
  const [applyingIdentityFor, setApplyingIdentityFor] = useState<Record<string, IdentityApplyInFlight>>({});
  const [identityNotice, setIdentityNotice] = useState<string | null>(null);

  // The Apply request stamp: the identity of the newest Apply started, so an
  // answer can tell its OWN entry from a newer request's that happens to name
  // the same member and part. A third counter beside the two read generations
  // because it answers a third question — which WRITE is this — and moves when
  // an Apply starts, for no other reason.
  const applyRequestTokenRef = useRef(0);

  // The account-write stamp: the identity of the newest write naming the
  // account on screen (`handleSave`, `handleDeactivate`), so an answer can tell
  // its OWN request from a later one that names the same account — a Save
  // overtaken by a Deactivate, which the account id alone cannot. ONE counter
  // for both, because both settle the same surface and own the same
  // screen-wide `isSaving`: with a counter each, a stale Save's cleanup would
  // clear the flag a live Deactivate has armed and re-enable the drawer's
  // buttons under a write still in flight. A fourth counter beside the three
  // above because it answers a fourth question — which ACCOUNT WRITE is this —
  // and moves when one starts, when the SELECTION moves, and for no other
  // reason.
  const accountWriteTokenRef = useRef(0);

  // The member-SAVE stamp: the identity of the newest member Save
  // (`handleSaveUser`), so an answer can tell its OWN request from a later one
  // naming the same member. Taken when a Save starts, bumped when the selection
  // moves.
  //
  // A counter for the member SAVE alone. Delete has no stamp of its own: nothing
  // is closed by a delete and no flag is armed by it, so its whole settle being
  // the member-list re-read, the account it names is the whole guard — and a
  // later member write reading an earlier Delete's own answer as stale is exactly
  // the swallow SOR-617 removes. This stamp, by contrast, is load-bearing: it is
  // what keeps a late answer from clearing an `isSavingUser` a newer Save owns.
  const memberSaveTokenRef = useRef(0);

  // Auth0 sync state
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<{ created: number; updated: number; skipped: number; errors: number } | null>(null);

  // Offices for service office dropdown
  const [offices, setOffices] = useState<Office[]>([]);

  const fetchAccounts = useCallback(async () => {
    // The generation is taken BEFORE the request, and its answer is dropped
    // when a newer list read has superseded it. Several callers re-read the
    // table without waiting for the last one — a completed save or deactivate
    // re-reads it unconditionally, and a search or filter change fires another
    // — so two reads can be out at once; without this, the older answer would
    // land last and repaint the table with the state the newer read replaced.
    const generation = ++accountListGenerationRef.current;
    const isCurrent = () => accountListGenerationRef.current === generation;
    setIsLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (statusFilter !== 'all') params.set('status', statusFilter);

      const data = await get<{ data: Account[]; pagination: { total: number } }>(`/api/admin/setup/accounts?${params}`);
      if (!isCurrent()) return;
      setAccounts(data.data || []);
      setTotal(data.pagination?.total ?? 0);
    } catch (err) {
      console.error('Failed to fetch accounts:', err);
      if (!isCurrent()) return;
      setError(err instanceof Error ? err.message : 'Failed to load accounts');
    } finally {
      // Only the newest read settles the spinner — an older answer discarded
      // above must not clear a flag a read still in flight has armed.
      if (isCurrent()) setIsLoading(false);
    }
  }, [get, search, statusFilter]);

  // The LATEST `fetchAccounts`, so a write whose answer lands after the search
  // box or the status filter moved re-reads the table the screen is showing NOW
  // — the query staff have typed since the request was sent — rather than the
  // one captured in the handler when it ran. A write handler outlives the render
  // it was created in: its `fetchAccounts` closure carries the search and filter
  // of THAT render, and calling it would issue a read with the query the screen
  // had at press time AND take a generation newer than the read the search
  // change already fired — so the stale answer would win and repaint the
  // unfiltered table under the typed search (SOR-617 review). Every write that
  // re-reads the table goes through this ref instead. Kept current in an effect,
  // never assigned during render, so it only ever holds a fetch its commit kept.
  const fetchAccountsRef = useRef(fetchAccounts);
  useEffect(() => {
    fetchAccountsRef.current = fetchAccounts;
  }, [fetchAccounts]);

  const fetchOffices = useCallback(async () => {
    try {
      const data = await get<{ data: Office[] }>('/api/admin/setup/offices');
      setOffices(data.data || []);
    } catch (error) {
      console.error('Failed to fetch offices:', error);
    }
  }, [get]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- TODO(deps-2026-07): newly detected by react-hooks bump, burn-down separately
    fetchAccounts();
  }, [fetchAccounts]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- TODO(deps-2026-07): newly detected by react-hooks bump, burn-down separately
    fetchOffices();
  }, [fetchOffices]);

  const handleSync = async () => {
    setIsSyncing(true);
    setSyncResult(null);
    setError(null);
    try {
      const result = await post<{ created: number; updated: number; skipped: number; errors: number }>(
        '/api/admin/setup/accounts/sync'
      );
      setSyncResult(result);
      // The LATEST read, not this handler's captured one: the sync can answer
      // long after it started, and the table must come back with the query the
      // screen holds now.
      fetchAccountsRef.current();
    } catch (err) {
      console.error('Sync failed:', err);
      setError(err instanceof Error ? err.message : 'Sync failed');
    } finally {
      setIsSyncing(false);
    }
  };

  // The member list and the identity differences are read TOGETHER — one
  // refresh for both, so every caller that reloads the list reloads the offers
  // beside it and no path updates one while leaving the other stale against
  // this account — but they are read and RENDERED INDEPENDENTLY, each painting
  // its own half as soon as its OWN read answers. The list is this account's
  // own rows; the differences are a fresh Auth0 read beside it, and that read
  // can be slow or fail on its own (Auth0 unconfigured, a cache reload failing)
  // without the member list being any less answerable. Held behind one
  // `Promise.allSettled`, the list would be withheld until the slowest of the
  // two answered — and so would every save that refreshes it; read apart, a
  // slow or failed differences read costs only the offers.
  //
  // EACH ANSWER IS DISCARDED WHEN THE DRAWER HAS MOVED ON. Both reads are for
  // the account selected when they started and can answer after the selection
  // changed — switching rows leaves the first account's reads in flight. So
  // every `setState` below is guarded, and an answer for an account nobody is
  // looking at cannot land under the one on screen.
  //
  // The guard is the refresh GENERATION, not the account id alone: two reads of
  // one account still have an order, and the older must not overwrite the newer
  // — a save's refresh overtaken by a later one, or a return to an account whose
  // first read is still slow. `requestedAccountIdRef` cannot tell those apart.
  // The generation moves on every read started here and on every selection
  // change, so an answer that lands under a generation other than its own is
  // stale by construction.
  const fetchAccountUsers = useCallback((accountId: string) => {
    // Checked BEFORE any state or request. A refresh answering after the drawer
    // has moved on — a member save, a delete or an Apply whose 200 lands once
    // another account is on screen — must not as much as arm the spinner: its
    // own answers are discarded below by that same guard, so the spinner would
    // stay armed with nothing left to disarm it.
    if (requestedAccountIdRef.current !== accountId) return;
    const generation = ++refreshGenerationRef.current;
    const isCurrent = () =>
      requestedAccountIdRef.current === accountId && refreshGenerationRef.current === generation;
    setIsLoadingUsers(true);
    const usersRead = get<{ data: AccountUser[] }>(`/api/admin/setup/accounts/${accountId}/users`);
    const differencesRead = get<IdentityDifferencesResponse>(
      `/api/admin/setup/accounts/${accountId}/users/identity-differences`,
    );
    void usersRead
      .then((answer) => {
        if (!isCurrent()) return;
        const freshUsers = answer.data || [];
        // The member the staff member is editing is folded into the fresh row —
        // untouched fields take the server's value, edited ones keep theirs —
        // rather than being replaced by it: the refresh happens behind an open
        // form (a save, a delete, an Apply that moved the member's email), and
        // replacing the form would drop an unsaved edit while leaving the form
        // holding a value the server has just replaced.
        //
        // WHICH member that is is read HERE, at answer time — never the
        // `editingUser` captured when this read started. A read fired while one
        // member was open can answer after staff have opened another (or hit
        // Add), and the captured id would then fold a stranger's row into the
        // form now on screen — and, with the form still untouched, overwrite
        // every field of it.
        const openMemberId = openMemberIdRef.current;
        const freshRow = openMemberId
          ? freshUsers.find((user) => user.id === openMemberId)
          : undefined;
        if (freshRow) {
          const baseline = memberBaselineRef.current;
          setUserFormData((form) => mergeFreshRecord(form, baseline, freshRow));
          memberBaselineRef.current = { ...freshRow };
        }
        setAccountUsers(freshUsers);
        setIsLoadingUsers(false);
      })
      .catch((reason: unknown) => {
        if (!isCurrent()) return;
        console.error('Failed to fetch account users:', reason);
        setIsLoadingUsers(false);
      });
    void differencesRead
      .then((answer) => {
        if (!isCurrent()) return;
        const byMember: Record<string, MemberIdentityDifference> = {};
        for (const row of answer.data ?? []) byMember[row.userId] = row;
        setIdentityDifferences(byMember);
        setIdentityUnchecked(new Set(answer.unchecked ?? []));
        setIdentityCheckFailed(false);
      })
      .catch((reason: unknown) => {
        if (!isCurrent()) return;
        console.error('Failed to fetch identity differences:', reason);
        // The offers are dropped with the failed read: a stale difference from a
        // previous account would put an Apply on the screen whose posted values
        // nobody just read. The failure is recorded BESIDE them, because the
        // read the table stands on did not happen — which is not the same answer
        // as "no member has anything to reconcile", and the empty maps alone
        // cannot tell the two apart.
        setIdentityDifferences({});
        setIdentityUnchecked(new Set());
        setIdentityCheckFailed(true);
      });
  }, [get]);

  /**
   * Read ONE account back and put the server's copy under the drawer — the
   * account the selected row stands for, and the Profile form that edits it.
   *
   * An Apply writes the account's own `contactEmail` when it was billing to the
   * member's old address, so the form is stale the moment one lands and a Save
   * taken from it would send the address the Apply just replaced. A call for an
   * account the staff member has already left issues NOTHING — no read and no
   * generation — so it cannot supersede the account on screen's read. A read
   * that IS issued is discarded when a LATER ACCOUNT READ has superseded it —
   * `accountReadGenerationRef`, never the member generation: a member refresh (a
   * save, a delete, a refused Apply) answers a different question and must not
   * void this one, or the Apply's re-read would be dropped and the stale address
   * sent back.
   */
  const refreshAccount = useCallback(async (accountId: string) => {
    // Checked BEFORE the generation is taken or the read is issued, exactly as
    // `fetchAccountUsers` checks it. A read for an account the drawer has left
    // is a no-op anyway, and taking the generation for one would still move it:
    // an Apply for the account left behind, answering while the account on
    // screen's OWN read-back is out, would supersede that read at the generation
    // check — discarding the server's copy of the account on screen and leaving
    // its Profile form holding the pre-Apply contact email a later Save sends
    // back (SOR-607).
    if (requestedAccountIdRef.current !== accountId) return;
    const generation = ++accountReadGenerationRef.current;
    try {
      const account = await get<Account>(`/api/admin/setup/accounts/${accountId}`);
      if (requestedAccountIdRef.current !== accountId) return;
      if (accountReadGenerationRef.current !== generation) return;
      setSelectedAccount(account);
      // Folded into the open form rather than replacing it: a field the staff
      // member has not edited takes the server's value, an edited one keeps
      // theirs, and the fresh record becomes the new baseline either way. The
      // baseline is captured before the write, because it is set after it and
      // the updater runs later still.
      const baseline = profileBaselineRef.current;
      setFormData((form) => mergeFreshRecord(form, baseline, account));
      profileBaselineRef.current = account;
    } catch (error) {
      console.error('Failed to read the account back:', error);
    }
  }, [get]);

  /**
   * Apply ONE offered part of a member's difference — the part whose own Apply
   * control was pressed.
   *
   * Part by part, not member by member: the member is offered whatever differs,
   * and staff confirm what they mean to keep. One control that posted every
   * offered part would make a member offering both the email and the names
   * unable to confirm the names without also carrying the address — the two are
   * decided independently by `planIdentityApply`, and the screen must be able
   * to ask for either alone.
   *
   * The body carries EXACTLY the `theirs` value that part's row shows — Auth0's,
   * as the read answered them — never a value re-derived here: the route
   * re-plans against a fresh Auth0 read and refuses (409) the WHOLE body when
   * any part it names is one the read no longer offers — nothing is written —
   * so sending anything but what was shown would be a claim the staff member
   * never made. A 409 is that answer: the read moved under this Apply, so both
   * the list and the differences are re-read rather than the Apply retried
   * blind.
   *
   * A 200 refreshes the list AND reads the account back. The Apply can have
   * written the account's own contact email (when it named the address the
   * account was billing to), so the Profile form's copy is stale the moment one
   * lands — a Save taken from it would send the address the Apply just
   * replaced. The account is re-read so the form and the selected account hold
   * the server's values.
   *
   * ANY OTHER ANSWER IS STATED. A failure that is not the 409 above — the fresh
   * Auth0 read that did not land (502), a permission refusal, a transport
   * failure — leaves the row untouched, and an Apply that visibly did nothing
   * and said nothing is the one outcome staff cannot act on. Only the 409
   * re-reads: it is the read that moved, and a reload is what corrects it;
   * nothing about the others is corrected by re-reading, so they are not.
   *
   * AN ANSWER BELONGS TO ITS ACCOUNT AND TO ITS REQUEST. The drawer can leave
   * this Apply's account while it is out, and the same member's same part can
   * be pressed again after the drawer returns — so an answer landing now may be
   * neither for the account on screen nor for the newest request. Both are
   * checked: the reads re-check the account themselves, the notices are held
   * behind the same account, and the in-flight entry is settled only by the
   * request whose token still stands.
   */
  const handleApplyIdentity = async (
    user: AccountUser,
    part: IdentityApplyPart,
    offer: MemberIdentityDifference,
  ) => {
    if (!selectedAccount) return;
    const accountId = selectedAccount.id;
    const body: IdentityApplyRequest = {};
    if (part === 'email' && offer.email) body.email = offer.email.theirs;
    if (part === 'names' && offer.names) body.names = { ...offer.names.theirs };
    const token = ++applyRequestTokenRef.current;
    setApplyingIdentityFor((inFlight) => ({ ...inFlight, [user.id]: { part, token } }));
    setIdentityNotice(null);
    try {
      await post(`/api/admin/setup/accounts/${accountId}/users/${user.id}/apply-identity`, body);
      // The part is settled the moment its own Apply answers: it leaves the
      // member's entry AT ONCE, without waiting for the differences re-read
      // below, so the control that was just pressed is gone rather than
      // pressable again for a value the member no longer differs by. Held
      // behind the account guard the catches below carry — the map written here
      // belongs to whatever account is on screen now, and an answer for an
      // account the drawer has left must not put this member's offer under it.
      if (requestedAccountIdRef.current === accountId) {
        setIdentityDifferences((byMember) => {
          const offer = byMember[user.id];
          if (!offer) return byMember;
          const settled = withoutAppliedPart(offer, part);
          const next = { ...byMember };
          if (settled) next[user.id] = settled;
          else delete next[user.id];
          return next;
        });
      }
      fetchAccountUsers(accountId);
      void refreshAccount(accountId);
    } catch (err) {
      // An answer for an account the drawer has left states nothing: the staff
      // member moved on while this Apply was out, and the notice belongs to
      // whatever account is on screen now — which this Apply never touched. The
      // reads below discard their own answers when the drawer has moved
      // (`fetchAccountUsers` and `refreshAccount` each check it themselves); the
      // notices have no such check, so the answer is dropped whole here.
      if (requestedAccountIdRef.current !== accountId) return;
      if (err instanceof ApiError && err.status === 409) {
        setIdentityNotice('This member changed since the difference was read — reloading the list.');
        fetchAccountUsers(accountId);
      } else {
        console.error('Failed to apply identity:', err);
        // The reason the Apply did not complete, as the failure carried it. The
        // list and the differences are NOT re-read: the read did not move under
        // this one, so a reload would repaint the same state and say nothing.
        setIdentityNotice(err instanceof Error ? err.message : 'The Apply did not complete.');
      }
    } finally {
      setApplyingIdentityFor((inFlight) => {
        // Settled only while THIS request's token still stands. The entry is the
        // member's own request, and a settle carrying another request's token is
        // not this entry's to clear: the part alone is the same across requests,
        // so matching on it would drop a live entry and re-enable the member's
        // controls with a write still in flight — the race this map closes.
        if (inFlight[user.id]?.token !== token) return inFlight;
        const { [user.id]: _settled, ...others } = inFlight;
        return others;
      });
    }
  };

  const handleRowClick = (account: Account) => {
    // This row is the account the member surface now stands for: every read
    // fired from here is stamped with it, and an answer for the account the
    // staff member left cannot land under this one. Both generations move with
    // the selection, so a read left in flight is stale the moment the drawer
    // moves — even if this same account is opened again before it answers.
    requestedAccountIdRef.current = account.id;
    refreshGenerationRef.current += 1;
    accountReadGenerationRef.current += 1;
    // The write stamps move with the selection, so a write left in flight is
    // stale the moment the drawer moves — even if this same account is opened
    // again before it answers, exactly as the two read generations above are.
    accountWriteTokenRef.current += 1;
    memberSaveTokenRef.current += 1;
    // The screen-wide write flags are cleared here, not by the handlers, which
    // no longer settle anything for a screen they were not started on: left
    // armed, they would disable the buttons of an account that owes no write.
    setIsSaving(false);
    setIsSavingUser(false);
    setSelectedAccount(account);
    setFormData(account);
    profileBaselineRef.current = account;
    setIsCreating(false);
    setActiveTab('profile');
    setAccountUsers([]);
    setIdentityDifferences({});
    setIdentityUnchecked(new Set());
    setIdentityCheckFailed(false);
    // The in-flight Apply map is deliberately NOT cleared here. It is keyed by
    // member id and each request settles its own entry in `finally` regardless
    // of the account on screen, so a member's entry travels with it across a
    // drawer move. Clearing it here instead would re-enable that member's
    // controls with its own Apply still out, letting a second concurrent Apply
    // for the member start — the race the map exists to close.
    setIdentityNotice(null);
    setEditingUser(null);
    // The drawer moved, so no member form is open: a member-list answer still in
    // flight for the account just left folds into nothing, and a member Save
    // left in flight no longer owns a form on the screen it has left behind.
    openMemberIdRef.current = null;
    openMemberFormStampRef.current = null;
    setIsCreatingUser(false);
    setIsDrawerOpen(true);
  };

  const handleAdd = () => {
    // No account is selected on the create path: clear the stamp so a read left
    // in flight for the account just left cannot land on the blank form, and
    // move both generations with it.
    requestedAccountIdRef.current = null;
    refreshGenerationRef.current += 1;
    accountReadGenerationRef.current += 1;
    accountWriteTokenRef.current += 1;
    memberSaveTokenRef.current += 1;
    // The create drawer inherits NOTHING from the account it replaces. No
    // account stands behind it, so a spinner a member-list read of that account
    // left armed would hang over a member surface with no read behind it, and
    // the read's own failure notice — or its offers — would be stated beside
    // members the read never examined. None of it is carried over: the member
    // surface of a create drawer starts empty, exactly as `handleRowClick`
    // leaves another account's.
    setIsLoadingUsers(false);
    setIdentityDifferences({});
    setIdentityUnchecked(new Set());
    setIdentityCheckFailed(false);
    setIdentityNotice(null);
    setEditingUser(null);
    openMemberIdRef.current = null;
    openMemberFormStampRef.current = null;
    setIsCreatingUser(false);
    setIsSaving(false);
    setIsSavingUser(false);
    setSelectedAccount(null);
    setFormData({
      accountNumber: '',
      name: '',
      accountType: 'organization',
      paymentMethod: 'invoice',
      paymentFrequency: 'per-order',
      paymentStatus: 'current',
      preAuthEnabled: false,
      processingFeePercent: 3.5,
      processingFeeMode: 'default' as const,
      processingFeeStatutory: true,
      processingFeeEfm: true,
      processingFeeService: true,
      retainerEnabled: false,
      retainerBalance: 0,
      retainerMinimum: 0,
      quickbooksSyncStatus: 'not-linked',
      showPricingAtCheckout: true,
      payInvoicesInGroups: false,
      paymentProcessingEnabled: true,
      status: 'active',
      address: {
        street1: '',
        city: '',
        state: '',
        zip: '',
        country: 'US',
      },
    });
    setIsCreating(true);
    setActiveTab('profile');
    setAccountUsers([]);
    setIsDrawerOpen(true);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name.startsWith('address.')) {
      const field = name.replace('address.', '');
      setFormData(prev => ({
        ...prev,
        address: {
          // The parts the Profile form edits, each as the empty string its
          // control renders when the record carries none. `country` is
          // deliberately NOT enumerated: the form has no control for it, so
          // spelling a default here would put a part the record never had into
          // the address the moment staff touch any other one — and an address
          // with nothing in it would stop reading as no address at all, so
          // typing into an empty field and clearing it again would send a blank
          // address the account never had (SOR-617). It rides the spread,
          // carried only when the record carries it, exactly as `street2` is.
          ...prev.address,
          street1: prev.address?.street1 || '',
          city: prev.address?.city || '',
          state: prev.address?.state || '',
          zip: prev.address?.zip || '',
          [field]: value,
        },
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: checked }));
  };

  const handleRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'preAuthEnabled') {
      setFormData(prev => ({ ...prev, preAuthEnabled: value === 'yes' }));
    } else if (name === 'processingFeeMode') {
      if (value === 'default') {
        // Reset to defaults when switching back to default mode
        setFormData(prev => ({
          ...prev,
          processingFeeMode: 'default' as const,
          processingFeePercent: 3.5,
          processingFeeStatutory: true,
          processingFeeEfm: true,
          processingFeeService: true,
        }));
      } else {
        setFormData(prev => ({ ...prev, processingFeeMode: value as 'default' | 'custom' }));
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: parseFloat(value) || 0 }));
  };

  const handleSave = async () => {
    // The account this Save was started for, and its own stamp — the same
    // pairing the two reads above use, for the same reason: the account id
    // alone cannot tell this Save from a later Deactivate on the same account,
    // and neither can it tell that the screen left and came back. `null` on the
    // create path, where no account stands behind the form.
    const accountId = selectedAccount?.id ?? null;
    const token = ++accountWriteTokenRef.current;
    setIsSaving(true);
    try {
      if (isCreating) {
        // A new account has no server copy to differ from: the whole form posts.
        await post('/api/admin/setup/accounts', formData);
      } else {
        // Only what the staff member changed since the form last took the
        // account from the server. A field they never touched is not in the
        // payload at all, so a record changed behind the open form — an Apply
        // that moved the contact email — is never written back by a Save.
        const payload = changedAccountFields(formData, profileBaselineRef.current);
        // Nothing changed is nothing to say: no request at all, and the drawer
        // closes exactly as a Save that had something to send does.
        if (Object.keys(payload).length > 0) {
          await patch(`/api/admin/setup/accounts/${accountId}`, payload);
        }
      }
      // A completed Save re-reads the table whatever has happened since — even
      // one that wrote nothing, which is the case a read-back behind the open
      // form makes necessary: the form took the server's values, but the ROW is
      // painted from the account list, which nothing has re-read since. The list
      // is the whole screen's, not this form's, so the refresh is not gated on
      // the drawer still standing on this account. It goes through
      // `fetchAccountsRef` — the LATEST read, carrying the search and filter the
      // screen holds now, never the query this handler captured at press time.
      // `fetchAccounts` discards an answer older than a newer table read, so
      // calling it is always safe.
      fetchAccountsRef.current();
      // The CLOSE, though, is this account's alone: an answer landing after the
      // drawer moved — or was moved and moved back — must not close the drawer
      // now on screen.
      if (requestedAccountIdRef.current !== accountId) return;
      if (accountWriteTokenRef.current !== token) return;
      setIsDrawerOpen(false);
    } catch (error) {
      console.error('Failed to save account:', error);
    } finally {
      // Only this request's own flag: a newer write owns the flag now, and a
      // moved screen cleared it on the selection change.
      if (accountWriteTokenRef.current === token) setIsSaving(false);
    }
  };

  const handleDeactivate = async () => {
    if (!selectedAccount) return;
    const accountId = selectedAccount.id;
    const token = ++accountWriteTokenRef.current;
    setIsSaving(true);
    try {
      await patch(`/api/admin/setup/accounts/${accountId}`, { status: 'inactive' });
      // The table is re-read exactly as `handleSave`'s is — the list is the whole
      // screen's, and a drawer that has moved is showing another account whose
      // row this write still changed nothing of, but the list carries both. It
      // goes through `fetchAccountsRef` for the same reason: the read must carry
      // the search and filter the screen holds NOW, not the ones captured here.
      // The close below, by contrast, is this account's alone.
      fetchAccountsRef.current();
      if (requestedAccountIdRef.current !== accountId) return;
      if (accountWriteTokenRef.current !== token) return;
      setIsDrawerOpen(false);
    } catch (error) {
      console.error('Failed to deactivate account:', error);
    } finally {
      if (accountWriteTokenRef.current === token) setIsSaving(false);
    }
  };

  // Tab switch handler — lazy-load users
  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    if (tab === 'accountUsers' && selectedAccount && accountUsers.length === 0) {
      fetchAccountUsers(selectedAccount.id);
    }
  };

  // ── Account user CRUD ──

  const handleAddUser = () => {
    setEditingUser(null);
    // No member stands behind the blank form, so a member-list answer landing
    // while it is open must fold nothing into it. A fresh form INSTANCE, though
    // — stamped so a Save taken from another blank form (or any other) cannot
    // mistake this one for its own and close it.
    openMemberIdRef.current = null;
    openMemberFormStampRef.current = ++memberFormStampSeqRef.current;
    setUserFormData({
      email: '',
      firstName: '',
      lastName: '',
      role: 'member',
      isAttorney: false,
      barNumber: '',
      status: 'active',
      auth0Id: '',
    });
    setIsCreatingUser(true);
  };

  const handleEditUser = (user: AccountUser) => {
    // Copies throughout, never the row object out of `accountUsers`: the list is
    // replaced wholesale on every refresh, so a form (or baseline) aliasing the
    // row would be the list's object rather than the staff member's edit of it.
    setEditingUser({ ...user });
    setUserFormData({ ...user });
    memberBaselineRef.current = { ...user };
    openMemberIdRef.current = user.id;
    // A fresh form INSTANCE, so an edit of this same member reopened after a
    // Cancel is not mistaken for the one a Save was started on.
    openMemberFormStampRef.current = ++memberFormStampSeqRef.current;
    setIsCreatingUser(true);
  };

  const handleUserInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setUserFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleUserCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setUserFormData(prev => ({ ...prev, [name]: checked }));
  };

  const handleSaveUser = async () => {
    if (!selectedAccount) return;
    // The account, the MEMBER and the FORM this Save was started for, all read
    // before any await. The member is what `editingUser` names right now — it
    // can be another member, or a blank new one, by the time this answers. The
    // form is stamped by its own identity, not by the member: a blank create
    // form has no member id and an edit of the same member reopened after a
    // Cancel carries the same id as before, so the id cannot tell THIS request's
    // form from a later one — only the stamp can.
    const accountId = selectedAccount.id;
    const memberId = editingUser?.id ?? null;
    const formStamp = openMemberFormStampRef.current;
    const token = ++memberSaveTokenRef.current;
    // Whether this Save had anything to say, on the same terms as the account
    // Save: a form with nothing changed sends no request and re-reads nothing.
    let wrote = false;
    setIsSavingUser(true);
    try {
      if (editingUser) {
        // Only the fields the staff member changed since the form last took the
        // member from the server. A member changed behind the open form — an
        // Apply that moved the email — is never written back by a Save, and
        // `auth0Id`, which the form has no control for, is never re-sent.
        const payload = changedMemberFields(userFormData, memberBaselineRef.current);
        // Nothing changed is nothing to say: no request at all, and the form
        // closes exactly as a Save that had something to send does.
        if (Object.keys(payload).length > 0) {
          await patch(`/api/admin/setup/accounts/${accountId}/users/${memberId}`, payload);
          wrote = true;
        }
      } else {
        // A new member has no server copy to differ from: the whole form posts,
        // carrying the pending Auth0 id a member starts with.
        await post(`/api/admin/setup/accounts/${accountId}/users`, {
          ...userFormData,
          auth0Id: userFormData.auth0Id || `pending|${Date.now()}`,
        });
        wrote = true;
      }
      // An answer for the account the drawer has left settles nothing, and the
      // account it names is the one the read below is for either way.
      if (requestedAccountIdRef.current !== accountId) return;
      // The list is this account's and is re-read whatever form is open — the
      // member read folds its own row into whichever member the form stands on
      // when the answer lands. It is NOT gated on this Save still being the
      // newest member write: the list belongs to the account, not to the form,
      // and `fetchAccountUsers` discards an answer for an account no longer
      // selected and one older than a newer read — so a Delete or another Save
      // starting or answering while this one was out cannot make calling it
      // wrong, only swallow a refresh the screen is owed (SOR-617).
      if (wrote) fetchAccountUsers(accountId);
      // The form closes only while the form INSTANCE on screen is still the one
      // this Save was started for. Opened on another member, or a blank one — or
      // this same member reopened after a Cancel — while the save was out, the
      // form now on screen is theirs and stays exactly as they left it. Compared
      // by the form's own stamp, never by the member id: the id is shared by
      // every blank create form (`null`) and by any reopen of the same member,
      // so a late answer would close a form the staff member is typing in.
      if (openMemberFormStampRef.current !== formStamp) return;
      setIsCreatingUser(false);
      setEditingUser(null);
      // Closed, so a re-read still in flight behind it folds into nothing — not
      // into whoever the staff member opens next — and no later answer reads
      // this form's stamp as its own.
      openMemberIdRef.current = null;
      openMemberFormStampRef.current = null;
    } catch (error) {
      console.error('Failed to save account user:', error);
    } finally {
      if (memberSaveTokenRef.current === token) setIsSavingUser(false);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!selectedAccount) return;
    // Nothing is closed by a delete and no form or flag is this request's to
    // settle, so the ACCOUNT is the whole guard: the member list below is the
    // account's, and it is re-read however many other member writes — a Save, or
    // another Delete — started or answered while this one was out. A stamp of its
    // own bought no settle and cost one: a later member write read an earlier
    // Delete's own answer as stale and swallowed its re-read, leaving the deleted
    // row standing (SOR-617).
    const accountId = selectedAccount.id;
    try {
      await del(`/api/admin/setup/accounts/${accountId}/users/${userId}`);
      if (requestedAccountIdRef.current !== accountId) return;
      fetchAccountUsers(accountId);
    } catch (error) {
      console.error('Failed to delete account user:', error);
    }
  };

  // The member-state control's anchor is the row being edited, or `pending`
  // for a row that does not exist yet (a new membership starts pending, and
  // staff may activate it). A reserved anchor leaves exactly one option, which
  // is what makes the control — and the whole form around it — read-only.
  const memberStatePrior = (editingUser?.status ?? 'pending') as MemberState;
  const memberStateOpts = memberStateOptions(memberStatePrior);
  const memberStateReadOnly = isReservedMemberState(memberStatePrior);

  const formatDate = (date: string | undefined) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const columns = [
    {
      key: 'accountNumber',
      header: 'Account #',
      width: '120px',
      render: (account: Account) => (
        <div className="flex items-center gap-2">
          <span className="font-medium" style={{ color: brand.colors.link }}>
            {account.accountNumber}
          </span>
          <SetupItemPreviewPopover
            brand={brand}
            title={account.name}
            fields={[
              { label: 'Account #', value: account.accountNumber },
              { label: 'Type', value: account.accountType },
              {
                label: 'Customer Category',
                // A React node keeps the popover's `value || '-'` fallback from
                // rewriting the blank-when-unset display (R18) to a dash.
                value: (
                  <span data-testid="preview-customer-category">
                    {customerCategoryDisplayLabel(account.customerCategory)}
                  </span>
                ),
              },
              { label: 'Contact', value: account.contactName || '-' },
              { label: 'Email', value: account.contactEmail || '-' },
              { label: 'Payment', value: account.paymentMethod },
              { label: 'Status', value: account.status },
            ]}
          />
        </div>
      ),
    },
    {
      key: 'name',
      header: 'Name & Address',
      render: (account: Account) => (
        <div>
          <div className="font-medium" style={{ color: brand.colors.text }}>
            {account.name}
          </div>
          {account.address && (
            <div className="text-xs" style={{ color: brand.colors.textLight }}>
              {account.address.city}, {account.address.state}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'customerCategory',
      header: 'Category',
      render: (account: Account) => (
        <span
          className="text-sm"
          style={{ color: brand.colors.text }}
          data-testid={`account-category-${account.id}`}
        >
          {customerCategoryDisplayLabel(account.customerCategory)}
        </span>
      ),
    },
    {
      key: 'contact',
      header: 'Contact',
      render: (account: Account) => (
        <div>
          <div className="text-sm" style={{ color: brand.colors.text }}>
            {account.contactName || '-'}
          </div>
          {account.contactEmail && (
            <div className="text-xs" style={{ color: brand.colors.textLight }}>
              {account.contactEmail}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'paymentMethod',
      header: 'Payment',
      render: (account: Account) => (
        <span className="capitalize text-sm">{account.paymentMethod.replace('-', ' ')}</span>
      ),
    },
    {
      key: 'paymentFrequency',
      header: 'Frequency',
      render: (account: Account) => (
        <span className="capitalize text-sm">{account.paymentFrequency.replace('-', ' ')}</span>
      ),
    },
    {
      key: 'paymentStatus',
      header: 'Payment Status',
      render: (account: Account) => <StatusBadge status={account.paymentStatus} />,
    },
    {
      key: 'retainer',
      header: 'Retainer',
      render: (account: Account) => (
        <span className="text-sm" style={{ color: brand.colors.text }}>
          {account.retainerEnabled ? `$${account.retainerBalance.toFixed(2)}` : '-'}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (account: Account) => <StatusBadge status={account.status} />,
    },
  ];

  const tabs: { key: TabType; label: string }[] = [
    { key: 'profile', label: 'Profile' },
    { key: 'paymentInfo', label: 'Payment Info' },
    { key: 'accountUsers', label: 'Account Users' },
  ];

  const officeOptions = offices.map(o => ({ value: o.id, label: o.name }));

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm font-medium" style={{ color: brand.colors.textLight }}>
          {total} Accounts
        </div>
        <button
          onClick={handleSync}
          disabled={isSyncing}
          className="px-3 py-1.5 rounded-lg text-sm font-medium transition-colors hover:opacity-90 disabled:opacity-50 border"
          style={{ borderColor: brand.colors.primary, color: brand.colors.link }}
        >
          {isSyncing ? 'Syncing...' : 'Sync from Auth0'}
        </button>
      </div>

      {syncResult && (
        <div className="mb-4 p-4 rounded-lg bg-blue-50 text-blue-800 text-sm">
          Sync complete: {syncResult.created} imported, {syncResult.updated} updated
          {syncResult.skipped > 0 && `, ${syncResult.skipped} skipped`}
          {syncResult.errors > 0 && `, ${syncResult.errors} errors`}
        </div>
      )}

      {error && (
        <div className="mb-4 p-4 rounded-lg bg-red-50 text-red-800 text-sm">
          {error}
        </div>
      )}

      {!isLoading && total === 0 && !error && (
        <div className="mb-4 p-6 rounded-lg text-center border border-dashed" style={{ borderColor: brand.colors.border }}>
          <p className="text-sm mb-3" style={{ color: brand.colors.textLight }}>
            No accounts found. Sync from Auth0 to import customer accounts.
          </p>
          <button
            onClick={handleSync}
            disabled={isSyncing}
            className="px-4 py-2 rounded-lg text-sm font-medium transition-colors hover:opacity-90 disabled:opacity-50"
            style={{ backgroundColor: brand.colors.primary, color: brand.colors.onPrimary }}
          >
            {isSyncing ? 'Syncing...' : 'Import from Auth0'}
          </button>
        </div>
      )}

      <SetupFilterBar
        brand={brand}
        Icons={Icons}
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search accounts..."
        statusFilter={statusFilter}
        statusOptions={statusFilterOptions}
        onStatusChange={setStatusFilter}
        onAdd={handleAdd}
        addLabel="New Account"
      />

      <SetupDataTable
        brand={brand}
        Icons={Icons}
        columns={columns}
        data={accounts}
        isLoading={isLoading}
        onRowClick={handleRowClick}
        emptyMessage="No accounts found"
        getRowId={(account) => account.id}
      />

      <SetupDetailDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        title={isCreating ? 'New Account' : 'Edit Account'}
        subtitle={selectedAccount?.accountNumber}
        brand={brand}
        Icons={Icons}
        width="xl"
        footer={
          activeTab === 'profile' ? (
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setIsDrawerOpen(false)}
                className="px-4 py-2 border rounded-lg text-sm font-medium transition-colors hover:bg-gray-50"
                style={{ borderColor: brand.colors.border, color: brand.colors.text }}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="px-4 py-2 rounded-lg text-sm font-medium transition-colors hover:opacity-90 disabled:opacity-50"
                style={{ backgroundColor: brand.colors.primary, color: brand.colors.onPrimary }}
              >
                {isSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          ) : undefined
        }
      >
        {/* Tabs */}
        <div
          className="flex border-b mb-6 -mx-6 px-6"
          style={{ borderColor: brand.colors.border }}
        >
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => handleTabChange(tab.key)}
              className={`px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${
                activeTab === tab.key ? '' : 'border-transparent'
              }`}
              style={{
                color: activeTab === tab.key ? brand.colors.link : brand.colors.textLight,
                borderColor: activeTab === tab.key ? brand.colors.primary : 'transparent',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* ═══════════════════ Profile Tab ═══════════════════ */}
        {activeTab === 'profile' && (
          <div className="space-y-6">
            {/* Header info */}
            {!isCreating && selectedAccount && (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <StatusBadge status={selectedAccount.status} />
                  {selectedAccount.status === 'active' && (
                    <button
                      onClick={handleDeactivate}
                      className="text-sm underline"
                      style={{ color: brand.colors.textLight }}
                    >
                      Deactivate
                    </button>
                  )}
                </div>
                <div className="text-right text-xs" style={{ color: brand.colors.textLight }}>
                  <div>QB: {selectedAccount.quickbooksSyncStatus}</div>
                  <div>Created: {formatDate(selectedAccount.createdAt)}</div>
                  <div>Modified: {formatDate(selectedAccount.updatedAt)}</div>
                </div>
              </div>
            )}

            {/* Core fields */}
            <div className="grid grid-cols-2 gap-4">
              <FormInput
                brand={brand}
                label="Account #"
                name="accountNumber"
                value={formData.accountNumber || ''}
                onChange={handleInputChange}
                required
                disabled={!isCreating}
              />
              <FormInput
                brand={brand}
                label="Name"
                name="name"
                value={formData.name || ''}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormSelect
                brand={brand}
                label="Account Type"
                name="accountType"
                value={formData.accountType || 'organization'}
                onChange={handleInputChange}
                options={accountTypeOptions}
              />
              <FormRadioGroup
                brand={brand}
                label="Type"
                name="accountType"
                value={formData.accountType || 'organization'}
                onChange={handleRadioChange}
                options={accountTypeOptions}
              />
            </div>

            <FormSelect
              brand={brand}
              label="Customer Category"
              name="customerCategory"
              value={formData.customerCategory || ''}
              onChange={handleInputChange}
              options={customerCategoryOptions}
            />

            {/* Payment fields */}
            <div
              className="border-t pt-4"
              style={{ borderColor: brand.colors.border }}
            >
              <h3 className="text-sm font-medium mb-3" style={{ color: brand.colors.text }}>
                Payment Configuration
              </h3>
              <FormSelect
                brand={brand}
                label="Payment Method"
                name="paymentMethod"
                value={formData.paymentMethod || 'invoice'}
                onChange={handleInputChange}
                options={paymentMethodOptions}
              />
              <div className="grid grid-cols-2 gap-4">
                <FormSelect
                  brand={brand}
                  label="Payment Method Status"
                  name="paymentStatus"
                  value={formData.paymentStatus || 'current'}
                  onChange={handleInputChange}
                  options={paymentStatusOptions}
                />
                <FormSelect
                  brand={brand}
                  label="Payment Frequency"
                  name="paymentFrequency"
                  value={formData.paymentFrequency || 'per-order'}
                  onChange={handleInputChange}
                  options={paymentFrequencyOptions}
                />
              </div>
              <FormRadioGroup
                brand={brand}
                label="Pre-Auth Required"
                name="preAuthEnabled"
                value={formData.preAuthEnabled ? 'yes' : 'no'}
                onChange={handleRadioChange}
                options={preAuthOptions}
              />
              {formData.preAuthEnabled ? (
                <div
                  className="ml-4 pl-4 border-l-2"
                  style={{ borderColor: brand.colors.primary }}
                >
                  <div
                    className="p-3 rounded-lg text-sm"
                    style={{ backgroundColor: brand.colors.bgLight, color: brand.colors.textLight }}
                  >
                    3.50% of Statutory Fees, EFM Fees, Service Fees
                  </div>
                </div>
              ) : (
                <div
                  className="ml-4 pl-4 border-l-2 space-y-3"
                  style={{ borderColor: brand.colors.border }}
                >
                  <FormRadioGroup
                    brand={brand}
                    label="Processing Fee Mode"
                    name="processingFeeMode"
                    value={formData.processingFeeMode || 'default'}
                    onChange={handleRadioChange}
                    options={processingFeeModeOptions}
                  />
                  {formData.processingFeeMode === 'custom' ? (
                    <>
                      <FormInput
                        brand={brand}
                        label="Processing Fee %"
                        name="processingFeePercent"
                        type="number"
                        value={formData.processingFeePercent ?? 3.5}
                        onChange={handleNumberChange}
                      />
                      <div className="space-y-2">
                        <span className="block text-sm font-medium" style={{ color: brand.colors.text }}>
                          Apply to Fee Types
                        </span>
                        <FormCheckbox
                          brand={brand}
                          label="Statutory Fees"
                          name="processingFeeStatutory"
                          checked={formData.processingFeeStatutory ?? true}
                          onChange={handleCheckboxChange}
                        />
                        <FormCheckbox
                          brand={brand}
                          label="EFM Fees"
                          name="processingFeeEfm"
                          checked={formData.processingFeeEfm ?? true}
                          onChange={handleCheckboxChange}
                        />
                        <FormCheckbox
                          brand={brand}
                          label="Service Fees"
                          name="processingFeeService"
                          checked={formData.processingFeeService ?? true}
                          onChange={handleCheckboxChange}
                        />
                      </div>
                    </>
                  ) : (
                    <div
                      className="p-3 rounded-lg text-sm"
                      style={{ backgroundColor: brand.colors.bgLight, color: brand.colors.textLight }}
                    >
                      3.50% of Statutory Fees, EFM Fees, Service Fees
                    </div>
                  )}
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <FormCheckbox
                  brand={brand}
                  label="Retainer Enabled"
                  name="retainerEnabled"
                  checked={formData.retainerEnabled || false}
                  onChange={handleCheckboxChange}
                />
                <FormCheckbox
                  brand={brand}
                  label="Show Pricing at Checkout"
                  name="showPricingAtCheckout"
                  checked={formData.showPricingAtCheckout ?? true}
                  onChange={handleCheckboxChange}
                />
              </div>
            </div>

            {/* Address */}
            <div
              className="border-t pt-4"
              style={{ borderColor: brand.colors.border }}
            >
              <h3 className="text-sm font-medium mb-3" style={{ color: brand.colors.text }}>
                Address
              </h3>
              <FormInput
                brand={brand}
                label="Street Address"
                name="address.street1"
                value={formData.address?.street1 || ''}
                onChange={handleInputChange}
              />
              <FormInput
                brand={brand}
                label="Suite/Floor"
                name="address.street2"
                value={formData.address?.street2 || ''}
                onChange={handleInputChange}
              />
              <div className="grid grid-cols-3 gap-4">
                <FormInput
                  brand={brand}
                  label="City"
                  name="address.city"
                  value={formData.address?.city || ''}
                  onChange={handleInputChange}
                />
                <FormInput
                  brand={brand}
                  label="State"
                  name="address.state"
                  value={formData.address?.state || ''}
                  onChange={handleInputChange}
                />
                <FormInput
                  brand={brand}
                  label="ZIP"
                  name="address.zip"
                  value={formData.address?.zip || ''}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            {/* Primary contact */}
            <div
              className="border-t pt-4"
              style={{ borderColor: brand.colors.border }}
            >
              <h3 className="text-sm font-medium mb-3" style={{ color: brand.colors.text }}>
                Primary Contact
              </h3>
              <div className="grid grid-cols-3 gap-4">
                <FormInput
                  brand={brand}
                  label="Name"
                  name="contactName"
                  value={formData.contactName || ''}
                  onChange={handleInputChange}
                />
                <FormInput
                  brand={brand}
                  label="Email"
                  name="contactEmail"
                  type="email"
                  value={formData.contactEmail || ''}
                  onChange={handleInputChange}
                />
                <FormInput
                  brand={brand}
                  label="Phone"
                  name="contactPhone"
                  type="tel"
                  value={formData.contactPhone || ''}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            {/* Secondary contact */}
            <div
              className="border-t pt-4"
              style={{ borderColor: brand.colors.border }}
            >
              <h3 className="text-sm font-medium mb-3" style={{ color: brand.colors.text }}>
                Secondary Contact
              </h3>
              <div className="grid grid-cols-3 gap-4">
                <FormInput
                  brand={brand}
                  label="Name"
                  name="secondaryContactName"
                  value={formData.secondaryContactName || ''}
                  onChange={handleInputChange}
                />
                <FormInput
                  brand={brand}
                  label="Email"
                  name="secondaryContactEmail"
                  type="email"
                  value={formData.secondaryContactEmail || ''}
                  onChange={handleInputChange}
                />
                <FormInput
                  brand={brand}
                  label="Phone"
                  name="secondaryContactPhone"
                  type="tel"
                  value={formData.secondaryContactPhone || ''}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            {/* Service / logistics */}
            <div
              className="border-t pt-4"
              style={{ borderColor: brand.colors.border }}
            >
              <h3 className="text-sm font-medium mb-3" style={{ color: brand.colors.text }}>
                Service &amp; Logistics
              </h3>
              <div className="grid grid-cols-3 gap-4">
                <FormSelect
                  brand={brand}
                  label="Service Office"
                  name="serviceOfficeId"
                  value={formData.serviceOfficeId || ''}
                  onChange={handleInputChange}
                  options={[{ value: '', label: '-- Select --' }, ...officeOptions]}
                />
                <FormInput
                  brand={brand}
                  label="Route"
                  name="route"
                  value={formData.route || ''}
                  onChange={handleInputChange}
                />
                <FormInput
                  brand={brand}
                  label="Pickup Time"
                  name="pickupTime"
                  value={formData.pickupTime || ''}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            {/* Notes */}
            <div
              className="border-t pt-4"
              style={{ borderColor: brand.colors.border }}
            >
              <FormTextarea
                brand={brand}
                label="Notes"
                name="notes"
                value={formData.notes || ''}
                onChange={handleInputChange}
                rows={3}
              />
            </div>

            {/* Additional options */}
            <div
              className="border-t pt-4"
              style={{ borderColor: brand.colors.border }}
            >
              <h3 className="text-sm font-medium mb-3" style={{ color: brand.colors.text }}>
                Additional Options
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <FormCheckbox
                  brand={brand}
                  label="Payment Processing Enabled"
                  name="paymentProcessingEnabled"
                  checked={formData.paymentProcessingEnabled ?? true}
                  onChange={handleCheckboxChange}
                />
                <FormCheckbox
                  brand={brand}
                  label="Pay Invoices in Groups"
                  name="payInvoicesInGroups"
                  checked={formData.payInvoicesInGroups || false}
                  onChange={handleCheckboxChange}
                />
              </div>
              <FormInput
                brand={brand}
                label="eFile Account Number"
                name="efileAccountNumber"
                value={formData.efileAccountNumber || ''}
                onChange={handleInputChange}
              />
              <FormSelect
                brand={brand}
                label="Status"
                name="status"
                value={formData.status || 'active'}
                onChange={handleInputChange}
                options={accountStatusOptions}
              />
            </div>
          </div>
        )}

        {/* ═══════════════════ Payment Info Tab ═══════════════════ */}
        {activeTab === 'paymentInfo' && (
          <div className="space-y-6">
            {/* Default payment info */}
            <div>
              <h3 className="text-sm font-medium mb-3" style={{ color: brand.colors.text }}>
                Default Payment Option
              </h3>
              <div
                className="p-4 rounded-lg"
                style={{ backgroundColor: brand.colors.bgLight }}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium capitalize" style={{ color: brand.colors.text }}>
                      {formData.paymentMethod?.replace('-', ' ') || 'Invoice'}
                    </div>
                    <div className="text-xs mt-1" style={{ color: brand.colors.textLight }}>
                      Frequency: {formData.paymentFrequency?.replace('-', ' ') || 'Per Order'}
                    </div>
                  </div>
                  <StatusBadge status={formData.paymentStatus || 'current'} />
                </div>
              </div>
            </div>

            {/* Quick actions */}
            <div className="flex gap-3">
              <button
                className="text-sm underline"
                style={{ color: brand.colors.link }}
                disabled
              >
                Grant Credit Terms
              </button>
              <button
                className="text-sm underline"
                style={{ color: brand.colors.link }}
                disabled
              >
                Disable ACH
              </button>
            </div>

            {/* Card entry form (placeholder) */}
            <div
              className="border-t pt-4"
              style={{ borderColor: brand.colors.border }}
            >
              <h3 className="text-sm font-medium mb-3" style={{ color: brand.colors.text }}>
                Add Payment Method
              </h3>
              <div className="space-y-4">
                <FormInput
                  brand={brand}
                  label="Card Number"
                  name="cardNumber"
                  value=""
                  onChange={() => {}}
                  placeholder="1234 5678 9012 3456"
                  disabled
                />
                <div className="grid grid-cols-2 gap-4">
                  <FormInput
                    brand={brand}
                    label="Expiry"
                    name="cardExpiry"
                    value=""
                    onChange={() => {}}
                    placeholder="MM/YY"
                    disabled
                  />
                  <FormInput
                    brand={brand}
                    label="CVC"
                    name="cardCvc"
                    value=""
                    onChange={() => {}}
                    placeholder="123"
                    disabled
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <FormInput
                    brand={brand}
                    label="Country"
                    name="cardCountry"
                    value="US"
                    onChange={() => {}}
                    disabled
                  />
                  <FormInput
                    brand={brand}
                    label="ZIP Code"
                    name="cardZip"
                    value=""
                    onChange={() => {}}
                    placeholder="90001"
                    disabled
                  />
                </div>
                <FormInput
                  brand={brand}
                  label="Billing Address"
                  name="billingAddress"
                  value=""
                  onChange={() => {}}
                  placeholder="123 Main St"
                  disabled
                />
                <FormInput
                  brand={brand}
                  label="Card Nickname"
                  name="cardNickname"
                  value=""
                  onChange={() => {}}
                  placeholder="e.g., Company Visa"
                  disabled
                />
                <div
                  className="p-4 rounded-lg text-center"
                  style={{ backgroundColor: brand.colors.bgLight }}
                >
                  <p className="text-sm" style={{ color: brand.colors.textLight }}>
                    Payment method management coming soon. Save and ACH operations are not yet available.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ═══════════════════ Account Users Tab ═══════════════════ */}
        {activeTab === 'accountUsers' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium" style={{ color: brand.colors.text }}>
                Account Users
              </h3>
              {!isCreatingUser && (
                <button
                  onClick={handleAddUser}
                  className="px-3 py-1.5 rounded-lg text-sm font-medium transition-colors hover:opacity-90"
                  style={{ backgroundColor: brand.colors.primary, color: brand.colors.onPrimary }}
                >
                  New Account User
                </button>
              )}
            </div>

            {/* User form (inline) */}
            {isCreatingUser && (
              <div
                className="p-4 rounded-lg border"
                style={{ borderColor: brand.colors.border, backgroundColor: brand.colors.bgLight }}
              >
                <h4 className="text-sm font-medium mb-3" style={{ color: brand.colors.text }}>
                  {editingUser ? 'Edit Account User' : 'New Account User'}
                </h4>
                {/* A reserved row is read-only: the writer refuses every staff
                    move out of `suspended`/`erased`, so the form is a view of
                    the row rather than an edit of it — every control is
                    disabled and the Save below is not offered at all. */}
                <div className="grid grid-cols-2 gap-4">
                  <FormInput
                    brand={brand}
                    label="Email"
                    name="email"
                    type="email"
                    value={userFormData.email || ''}
                    onChange={handleUserInputChange}
                    required
                    disabled={memberStateReadOnly}
                  />
                  <FormInput
                    brand={brand}
                    label="First Name"
                    name="firstName"
                    value={userFormData.firstName || ''}
                    onChange={handleUserInputChange}
                    disabled={memberStateReadOnly}
                  />
                  <FormInput
                    brand={brand}
                    label="Last Name"
                    name="lastName"
                    value={userFormData.lastName || ''}
                    onChange={handleUserInputChange}
                    disabled={memberStateReadOnly}
                  />
                  <FormSelect
                    brand={brand}
                    label="Role"
                    name="role"
                    value={userFormData.role || 'member'}
                    onChange={handleUserInputChange}
                    options={accountUserRoleOptions}
                    disabled={memberStateReadOnly}
                  />
                </div>
                <FormCheckbox
                  brand={brand}
                  label="Is Attorney"
                  name="isAttorney"
                  checked={userFormData.isAttorney || false}
                  onChange={handleUserCheckboxChange}
                  disabled={memberStateReadOnly}
                />
                {userFormData.isAttorney && (
                  <FormInput
                    brand={brand}
                    label="Bar Number"
                    name="barNumber"
                    value={userFormData.barNumber || ''}
                    onChange={handleUserInputChange}
                    disabled={memberStateReadOnly}
                  />
                )}
                <FormSelect
                  brand={brand}
                  label="Status"
                  name="status"
                  value={userFormData.status || 'active'}
                  onChange={handleUserInputChange}
                  options={memberStateOpts}
                  disabled={memberStateReadOnly}
                  helpText={
                    memberStateReadOnly
                      ? 'Reserved state — only an erasure or intake action can move this membership.'
                      : undefined
                  }
                />
                <div className="flex justify-end gap-3 mt-4">
                  <button
                    onClick={() => {
                      setIsCreatingUser(false);
                      setEditingUser(null);
                      openMemberIdRef.current = null;
                      // The form instance is gone: a Save left in flight for it
                      // no longer owns whatever form is opened next.
                      openMemberFormStampRef.current = null;
                    }}
                    className="px-3 py-1.5 border rounded-lg text-sm font-medium transition-colors hover:bg-gray-50"
                    style={{ borderColor: brand.colors.border, color: brand.colors.text }}
                  >
                    Cancel
                  </button>
                  {/* A reserved row has nothing to save — the controls above
                      are all disabled — so the form offers no Save at all. */}
                  {!memberStateReadOnly && (
                    <button
                      onClick={handleSaveUser}
                      disabled={isSavingUser}
                      className="px-3 py-1.5 rounded-lg text-sm font-medium transition-colors hover:opacity-90 disabled:opacity-50"
                      style={{ backgroundColor: brand.colors.primary, color: brand.colors.onPrimary }}
                    >
                      {isSavingUser ? 'Saving...' : 'Save'}
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* A read that failed is not an empty answer — rendered alike, every
                member would look reconciled. Said once, above the table it is
                about, and replaced by the offers the moment a read answers. */}
            {identityCheckFailed && (
              <div
                className="text-xs"
                data-testid="identity-read-notice"
                style={{ color: brand.colors.textLight }}
              >
                The Auth0 check could not be made — the members&apos; differences are unknown.
              </div>
            )}

            {/* A refused Apply (409) is not an error to retry: the difference
                moved under it, and the list beside this line is being re-read.
                An Apply answered any other way is the failure it did not
                complete through, stated here beside the same line. */}
            {identityNotice && (
              <div
                className="text-xs"
                data-testid="identity-apply-notice"
                style={{ color: brand.colors.textLight }}
              >
                {identityNotice}
              </div>
            )}

            {/* Users table */}
            {isLoadingUsers ? (
              <div
                data-testid="account-users-loading"
                className="flex items-center justify-center py-8"
              >
                <div
                  className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin"
                  style={{ borderColor: brand.colors.primary, borderTopColor: 'transparent' }}
                />
              </div>
            ) : accountUsers.length === 0 ? (
              <div className="py-8 text-center text-sm" style={{ color: brand.colors.textLight }}>
                No users linked to this account
              </div>
            ) : (
              <div className="overflow-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr style={{ borderBottom: `1px solid ${brand.colors.border}` }}>
                      <th className="text-left py-2 px-3 font-medium" style={{ color: brand.colors.textLight }}>Name</th>
                      <th className="text-left py-2 px-3 font-medium" style={{ color: brand.colors.textLight }}>Email</th>
                      <th className="text-left py-2 px-3 font-medium" style={{ color: brand.colors.textLight }}>Role</th>
                      <th className="text-left py-2 px-3 font-medium" style={{ color: brand.colors.textLight }}>Attorney</th>
                      <th className="text-left py-2 px-3 font-medium" style={{ color: brand.colors.textLight }}>Bar #</th>
                      <th className="text-left py-2 px-3 font-medium" style={{ color: brand.colors.textLight }}>Status</th>
                      <th className="text-left py-2 px-3 font-medium" style={{ color: brand.colors.textLight }}>Auth0 Difference</th>
                      <th className="text-left py-2 px-3 font-medium" style={{ color: brand.colors.textLight }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {accountUsers.map((user) => (
                      <tr
                        key={user.id}
                        style={{ borderBottom: `1px solid ${brand.colors.border}` }}
                      >
                        <td className="py-2 px-3" style={{ color: brand.colors.text }}>
                          {user.firstName} {user.lastName}
                        </td>
                        <td className="py-2 px-3" style={{ color: brand.colors.text }}>
                          {user.email}
                        </td>
                        <td className="py-2 px-3 capitalize" style={{ color: brand.colors.text }}>
                          {user.role}
                        </td>
                        <td className="py-2 px-3" style={{ color: brand.colors.text }}>
                          {user.isAttorney ? 'Yes' : 'No'}
                        </td>
                        <td className="py-2 px-3" style={{ color: brand.colors.text }}>
                          {user.barNumber || '-'}
                        </td>
                        <td className="py-2 px-3">
                          <StatusBadge status={user.status} />
                        </td>
                        {/* What this member differs from Auth0 by, part by part:
                            ours beside theirs, and ONE Apply PER OFFERED PART —
                            the parts are confirmed independently, so a member
                            offered both must be able to confirm either alone.
                            A member with no offered part renders nothing here —
                            neither values nor a control — because there is
                            nothing to reconcile and so nothing to apply.

                            A member the read could not check takes the cell
                            instead: its difference is unknown, and "not checked"
                            is a different answer from the empty cell above,
                            which claims there is nothing to reconcile. The two
                            never share a member — but the unchecked fact is the
                            one the read could not complete, so it wins. */}
                        <td className="py-2 px-3 align-top">
                          {identityUnchecked.has(user.id) ? (
                            <span
                              className="text-xs"
                              data-testid={`identity-unchecked-${user.id}`}
                              style={{ color: brand.colors.textLight }}
                            >
                              not checked
                            </span>
                          ) : (
                            identityDifferences[user.id] && (
                              <div
                                className="space-y-1"
                                data-testid={`identity-difference-${user.id}`}
                              >
                                {identityDifferences[user.id].email && (
                                  <div
                                    className="text-xs"
                                    data-testid={`identity-offer-email-${user.id}`}
                                  >
                                    <span style={{ color: brand.colors.textLight }}>Email </span>
                                    <span
                                      data-testid={`identity-email-ours-${user.id}`}
                                      style={{ color: brand.colors.textLight }}
                                    >
                                      {identityDifferences[user.id].email!.ours}
                                    </span>
                                    <span style={{ color: brand.colors.textLight }}> → </span>
                                    <span
                                      data-testid={`identity-email-theirs-${user.id}`}
                                      style={{ color: brand.colors.text }}
                                    >
                                      {identityDifferences[user.id].email!.theirs}
                                    </span>
                                    <button
                                      onClick={() =>
                                        handleApplyIdentity(user, 'email', identityDifferences[user.id])
                                      }
                                      disabled={applyingIdentityFor[user.id] !== undefined}
                                      aria-label={`Apply the offered email for ${user.firstName} ${user.lastName}`}
                                      data-testid={`apply-identity-email-${user.id}`}
                                      className="ml-2 text-xs underline disabled:opacity-50"
                                      style={{ color: brand.colors.link }}
                                    >
                                      {applyingIdentityFor[user.id]?.part === 'email' ? 'Applying…' : 'Apply'}
                                    </button>
                                  </div>
                                )}
                                {identityDifferences[user.id].names && (
                                  <div
                                    className="text-xs"
                                    data-testid={`identity-offer-names-${user.id}`}
                                  >
                                    <span style={{ color: brand.colors.textLight }}>Name </span>
                                    <span
                                      data-testid={`identity-names-ours-${user.id}`}
                                      style={{ color: brand.colors.textLight }}
                                    >
                                      {displayName(
                                        identityDifferences[user.id].names!.ours.firstName,
                                        identityDifferences[user.id].names!.ours.lastName,
                                      )}
                                    </span>
                                    <span style={{ color: brand.colors.textLight }}> → </span>
                                    <span
                                      data-testid={`identity-names-theirs-${user.id}`}
                                      style={{ color: brand.colors.text }}
                                    >
                                      {displayName(
                                        identityDifferences[user.id].names!.theirs.firstName,
                                        identityDifferences[user.id].names!.theirs.lastName,
                                      )}
                                    </span>
                                    <button
                                      onClick={() =>
                                        handleApplyIdentity(user, 'names', identityDifferences[user.id])
                                      }
                                      disabled={applyingIdentityFor[user.id] !== undefined}
                                      aria-label={`Apply the offered name for ${user.firstName} ${user.lastName}`}
                                      data-testid={`apply-identity-names-${user.id}`}
                                      className="ml-2 text-xs underline disabled:opacity-50"
                                      style={{ color: brand.colors.link }}
                                    >
                                      {applyingIdentityFor[user.id]?.part === 'names' ? 'Applying…' : 'Apply'}
                                    </button>
                                  </div>
                                )}
                              </div>
                            )
                          )}
                        </td>
                        <td className="py-2 px-3">
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleEditUser(user)}
                              className="text-xs underline"
                              style={{ color: brand.colors.link }}
                            >
                              Edit
                            </button>
                            {/* A row the writer's `notErased` predicate puts
                                beyond every statement cannot be deleted — and
                                an erased row IS the record that the erasure
                                happened — so a reserved row offers no Delete.
                                The predicate is the control's own: it is
                                derived from the one decider, never listed. */}
                            {!isReservedMemberState(user.status) && (
                              <button
                                onClick={() => handleDeleteUser(user.id)}
                                className="text-xs underline text-red-600"
                              >
                                Delete
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </SetupDetailDrawer>
    </div>
  );
};

export default SetupAccounts;
